/**
 * Source & Evidence Wizard Component
 * 
 * Single unified flow for adding Source (Governance) + Evidence (Content)
 * Steps: Mode -> Source Definition -> Evidence -> Duplicate Check -> Execute
 * 
 * Part B: Uses PrecedenceRankSelector (preset scale) instead of numeric input
 */
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription, CardFooter } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Progress } from '../ui/progress';
import { 
  Upload, 
  Globe, 
  FileText, 
  CheckCircle, 
  AlertTriangle,
  ArrowRight,
  ArrowLeft,
  Loader2,
  Copy,
  ExternalLink,
  Shield,
  Layers,
  RefreshCw,
  Database
} from 'lucide-react';
import { toast } from 'sonner';
import PrecedenceRankSelector from './PrecedenceRankSelector';
import { adminFetchHeaders } from '@/hooks/useAdminFetch';

const API = window.location.origin;

const getAuthHeaders = () => adminFetchHeaders(false);

// Mode options
const WIZARD_MODES = [
  {
    value: 'UPLOAD_DOCUMENT',
    label: 'Dokument hochladen',
    description: 'PDF, Word, oder andere Dokumente',
    icon: Upload,
    color: 'bg-blue-100 text-blue-700 border-blue-300'
  },
  {
    value: 'WEB_PDF_SNAPSHOT',
    label: 'Web PDF Snapshot',
    description: 'Webseite als PDF erfassen',
    icon: FileText,
    color: 'bg-green-100 text-green-700 border-green-300'
  },
  {
    value: 'WEB_CRAWL_SINGLE',
    label: 'Web Snapshot (Single Page)',
    description: 'Einzelne Webseite crawlen',
    icon: Globe,
    color: 'bg-purple-100 text-purple-700 border-purple-300'
  },
  {
    value: 'WEB_CRAWL_MULTI',
    label: 'Web Snapshot (Multi Page)',
    description: 'Mehrere Seiten crawlen',
    icon: Layers,
    color: 'bg-orange-100 text-orange-700 border-orange-300'
  }
];

// Tier options
const TIERS = [
  { value: 'PRIMARY', label: 'PRIMARY', description: 'Offizielle Guides, Standards', color: 'bg-green-100' },
  { value: 'SECONDARY', label: 'SECONDARY', description: 'Supplements, FAQs', color: 'bg-yellow-100' },
  { value: 'TERTIARY', label: 'TERTIARY', description: 'Training, Blogs', color: 'bg-gray-100' }
];

// REMOVED: Hardcoded DEFAULT_EXAMS - now loaded dynamically from backend
// REMOVED: Hardcoded FRAMEWORKS - framework_id is auto-derived from exam_id

const LANGUAGES = [
  { code: 'de', name: 'Deutsch' },
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'fr', name: 'Français' }
];

const SourceEvidenceWizard = ({ isOpen, onClose, onSuccess }) => {
  // Wizard state
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Dynamic exam list from backend
  const [availableExams, setAvailableExams] = useState([]);
  const [examsLoading, setExamsLoading] = useState(false);
  
  // Step 0: Mode
  const [mode, setMode] = useState('');
  
  // Step 1: Source Definition
  const [sourceData, setSourceData] = useState({
    display_name: '',
    exam_ids: [],
    framework_ids: [], // Auto-set based on exam selection
    language: 'de',
    tier: 'TERTIARY',
    precedence_rank: 900,  // Default: Primary preset
    edition: '',
    version: '',
    release_date: '',
    notes: '',
    tags: []
  });
  
  // Step 2: Evidence
  const [file, setFile] = useState(null);
  const [webConfig, setWebConfig] = useState({
    canonical_url: '',
    render_mode: 'STATIC_HTTP',
    crawl_policy: {
      domain_allowlist: [],
      max_depth: 2,
      max_pages: 20,
      same_language_only: true
    }
  });
  
  // Step 3: Duplicate Check Result
  const [duplicateResult, setDuplicateResult] = useState(null);
  const [selectedAction, setSelectedAction] = useState('NEW_SOURCE');
  
  // P0.7: AKU Extraction toggle (after Step 3)
  const [runAkuExtraction, setRunAkuExtraction] = useState(false);
  const [akuGateStatus, setAkuGateStatus] = useState(null);
  const [checkingAkuGate, setCheckingAkuGate] = useState(false);
  
  // Step 4: Result
  const [wizardResult, setWizardResult] = useState(null);
  
  // Load exams from backend on mount
  // P2: Now uses course-centric endpoint for unified data loading
  useEffect(() => {
    const loadExams = async () => {
      setExamsLoading(true);
      try {
        // P2: Use course-centric endpoint
        const response = await fetch(`${API}/api/admin/courses-with-details?include_stats=false`, {
          headers: getAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          // Transform courses to exam format for backwards compatibility
          const exams = data.courses.map(course => ({
            exam_id: course.exam_profile_id,
            name: course.name,
            framework_id: course.framework_id,
            course_id: course.course_id,
            is_active: course.is_active
          })).filter(e => e.is_active !== false);
          setAvailableExams(exams);
        } else {
          // Fallback to legacy endpoint
          const fallbackResponse = await fetch(`${API}/api/question-engine/exams-with-framework`, {
            headers: getAuthHeaders()
          });
          if (fallbackResponse.ok) {
            const data = await fallbackResponse.json();
            setAvailableExams(data);
          } else {
            console.error('Failed to load exams:', response.status);
            toast.error('Konnte Prüfungen nicht laden');
          }
        }
      } catch (error) {
        console.error('Error loading exams:', error);
        toast.error('Fehler beim Laden der Prüfungen');
      } finally {
        setExamsLoading(false);
      }
    };
    
    if (isOpen) {
      loadExams();
    }
  }, [isOpen]);
  
  // Auto-set framework_ids when exam_ids change
  useEffect(() => {
    if (sourceData.exam_ids.length > 0 && availableExams.length > 0) {
      // Get unique framework_ids from selected exams
      const frameworkIds = [...new Set(
        sourceData.exam_ids
          .map(examId => {
            const exam = availableExams.find(e => e.exam_id === examId);
            return exam?.framework_id;
          })
          .filter(Boolean)
      )];
      
      setSourceData(prev => ({
        ...prev,
        framework_ids: frameworkIds
      }));
    }
  }, [sourceData.exam_ids, availableExams]);
  
  // Reset wizard
  const resetWizard = useCallback(() => {
    setStep(0);
    setMode('');
    setSourceData({
      display_name: '',
      exam_ids: [],
      framework_ids: [],
      language: 'de',
      tier: 'TERTIARY',
      precedence_rank: 900,  // Default: Primary preset
      edition: '',
      version: '',
      release_date: '',
      notes: '',
      tags: []
    });
    setFile(null);
    setWebConfig({
      canonical_url: '',
      render_mode: 'STATIC_HTTP',
      crawl_policy: { domain_allowlist: [], max_depth: 2, max_pages: 20, same_language_only: true }
    });
    setDuplicateResult(null);
    setSelectedAction('NEW_SOURCE');
    setRunAkuExtraction(false);
    setAkuGateStatus(null);
    setWizardResult(null);
  }, []);
  
  // Step navigation
  const canProceed = () => {
    switch (step) {
      case 0: return !!mode;
      case 1: return sourceData.display_name && sourceData.exam_ids.length > 0;
      case 2: 
        if (mode === 'UPLOAD_DOCUMENT') return !!file;
        return !!webConfig.canonical_url;
      case 3: return !!selectedAction;
      default: return false;
    }
  };
  
  const handleNext = async () => {
    if (step === 2) {
      // Run duplicate check before step 3
      await checkDuplicates();
    } else if (step === 3) {
      // Execute wizard
      await executeWizard();
    } else {
      setStep(s => s + 1);
    }
  };
  
  const handleBack = () => {
    if (step > 0) setStep(s => s - 1);
  };
  
  // P0.7: Check AKU Gate status when entering Step 3
  const checkAkuGateStatus = async () => {
    if (!sourceData.exam_ids || sourceData.exam_ids.length === 0) {
      setAkuGateStatus({
        gate_passed: false,
        can_enable_toggle: false,
        failure_reason: 'NO_EXAM_ID',
        message: 'Keine Prüfung ausgewählt'
      });
      return;
    }
    
    setCheckingAkuGate(true);
    try {
      const examId = sourceData.exam_ids[0];
      const response = await fetch(`${API}/api/source-registry/wizard/aku-gate-status/${examId}`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const result = await response.json();
        setAkuGateStatus(result);
        // Auto-disable toggle if gate fails
        if (!result.can_enable_toggle) {
          setRunAkuExtraction(false);
        }
      } else {
        setAkuGateStatus({
          gate_passed: false,
          can_enable_toggle: false,
          failure_reason: 'ERROR',
          message: 'Fehler beim Prüfen des AKU-Gates'
        });
      }
    } catch (error) {
      setAkuGateStatus({
        gate_passed: false,
        can_enable_toggle: false,
        failure_reason: 'NETWORK_ERROR',
        message: error.message
      });
    } finally {
      setCheckingAkuGate(false);
    }
  };
  
  // Step 3: Check duplicates
  const checkDuplicates = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('mode', mode);
      formData.append('source_json', JSON.stringify(sourceData));
      
      if (mode !== 'UPLOAD_DOCUMENT' && webConfig.canonical_url) {
        formData.append('web_config_json', JSON.stringify(webConfig));
      }
      
      if (file) {
        formData.append('file', file);
      }
      
      const response = await fetch(`${API}/api/source-registry/wizard/check-duplicate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData
      });
      
      if (response.ok) {
        const result = await response.json();
        setDuplicateResult(result);
        setSelectedAction(result.suggested_action);
        setStep(3);
        // P0.7: Check AKU gate status after duplicate check
        checkAkuGateStatus();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler bei der Duplikat-Prüfung');
      }
    } catch (error) {
      toast.error('Netzwerkfehler: ' + error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Step 4: Execute wizard
  const executeWizard = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('mode', mode);
      formData.append('source_json', JSON.stringify(sourceData));
      formData.append('duplicate_action', selectedAction);
      formData.append('run_aku_extraction', runAkuExtraction.toString());  // P0.7
      
      if (duplicateResult?.existing_source_id) {
        formData.append('existing_source_id', duplicateResult.existing_source_id);
      }
      
      if (mode !== 'UPLOAD_DOCUMENT' && webConfig.canonical_url) {
        formData.append('web_config_json', JSON.stringify(webConfig));
      }
      
      if (file) {
        formData.append('file', file);
      }
      
      const response = await fetch(`${API}/api/source-registry/wizard/execute`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData
      });
      
      if (response.ok) {
        const result = await response.json();
        setWizardResult(result);
        setStep(4);
        toast.success('Source & Evidence erfolgreich erstellt!');
        if (onSuccess) onSuccess(result);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler bei der Ausführung');
      }
    } catch (error) {
      toast.error('Netzwerkfehler: ' + error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle file selection
  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };
  
  // Toggle exam selection
  const toggleExam = (examId) => {
    setSourceData(prev => ({
      ...prev,
      exam_ids: prev.exam_ids.includes(examId)
        ? prev.exam_ids.filter(id => id !== examId)
        : [...prev.exam_ids, examId]
    }));
  };
  
  // REMOVED: toggleFramework - framework_ids are auto-set based on exam selection
  
  // Group exams by framework for better UX
  const examsByFramework = useMemo(() => {
    const grouped = {};
    availableExams.forEach(exam => {
      const fw = exam.framework_id || 'OTHER';
      if (!grouped[fw]) grouped[fw] = [];
      grouped[fw].push(exam);
    });
    return grouped;
  }, [availableExams]);
  
  // Render step content
  const renderStepContent = () => {
    switch (step) {
      case 0:
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Wählen Sie den Typ der Evidence-Quelle:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {WIZARD_MODES.map((m) => {
                const Icon = m.icon;
                const isSelected = mode === m.value;
                return (
                  <button
                    key={m.value}
                    onClick={() => setMode(m.value)}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      isSelected 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid={`mode-${m.value}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${m.color}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="font-medium">{m.label}</div>
                        <div className="text-sm text-gray-500">{m.description}</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );
        
      case 1:
        return (
          <div className="space-y-4">
            {/* Display Name */}
            <div>
              <Label>Name der Quelle *</Label>
              <Input
                value={sourceData.display_name}
                onChange={(e) => setSourceData({...sourceData, display_name: e.target.value})}
                placeholder="z.B. Scrum Guide 2020 (DE)"
                data-testid="source-display-name"
              />
              <p className="text-xs text-gray-500 mt-1">
                Der canonical_name wird automatisch generiert
              </p>
            </div>
            
            {/* Exam Selection - Dynamic from Backend */}
            <div>
              <Label>Prüfungen * <span className="text-xs text-gray-400">(Framework wird automatisch zugeordnet)</span></Label>
              {examsLoading ? (
                <div className="flex items-center gap-2 text-sm text-gray-500 mt-2">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Lade Prüfungen...
                </div>
              ) : (
                <div className="space-y-3 mt-2">
                  {Object.entries(examsByFramework).map(([framework, exams]) => (
                    <div key={framework} className="space-y-1">
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        {framework}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {exams.map(exam => (
                          <Badge
                            key={exam.exam_id}
                            variant={sourceData.exam_ids.includes(exam.exam_id) ? 'default' : 'outline'}
                            className="cursor-pointer hover:bg-blue-50"
                            onClick={() => toggleExam(exam.exam_id)}
                            title={exam.description || exam.name}
                          >
                            {exam.exam_id}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Show auto-derived frameworks as read-only badges */}
              {sourceData.framework_ids.length > 0 && (
                <div className="mt-3 flex items-center gap-2">
                  <span className="text-xs text-gray-500">Framework:</span>
                  {sourceData.framework_ids.map(fw => (
                    <Badge key={fw} variant="secondary" className="text-xs">
                      {fw}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            
            {/* Language and Tier Row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Sprache</Label>
                <Select 
                  value={sourceData.language}
                  onValueChange={(v) => setSourceData({...sourceData, language: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LANGUAGES.map(lang => (
                      <SelectItem key={lang.code} value={lang.code}>
                        {lang.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Tier</Label>
                <Select 
                  value={sourceData.tier}
                  onValueChange={(v) => setSourceData({...sourceData, tier: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIERS.map(t => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label} - {t.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Precedence Rank - Using Preset Selector */}
            <PrecedenceRankSelector
              value={sourceData.precedence_rank}
              onChange={(newValue) => setSourceData({...sourceData, precedence_rank: newValue})}
              tier={sourceData.tier}
              data-testid="source-precedence-selector"
            />
            
            {/* Optional fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Edition/Version</Label>
                <Input
                  value={sourceData.edition}
                  onChange={(e) => setSourceData({...sourceData, edition: e.target.value})}
                  placeholder="z.B. 2020, 7th Edition"
                />
              </div>
              <div>
                <Label>Release-Datum</Label>
                <Input
                  type="date"
                  value={sourceData.release_date}
                  onChange={(e) => setSourceData({...sourceData, release_date: e.target.value})}
                />
              </div>
            </div>
            
            <div>
              <Label>Notizen</Label>
              <Textarea
                value={sourceData.notes}
                onChange={(e) => setSourceData({...sourceData, notes: e.target.value})}
                placeholder="Optionale Notizen..."
                rows={2}
              />
            </div>
          </div>
        );
        
      case 2:
        return (
          <div className="space-y-4">
            {mode === 'UPLOAD_DOCUMENT' ? (
              <div>
                <Label>Dokument hochladen *</Label>
                <div className="mt-2">
                  <div 
                    className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                      file ? 'border-green-300 bg-green-50' : 'border-gray-300 hover:border-blue-400'
                    }`}
                  >
                    {file ? (
                      <div className="space-y-2">
                        <CheckCircle className="w-12 h-12 mx-auto text-green-500" />
                        <p className="font-medium">{file.name}</p>
                        <p className="text-sm text-gray-500">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setFile(null)}
                        >
                          Anderes Dokument wählen
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Upload className="w-12 h-12 mx-auto text-gray-400" />
                        <p>Datei hierher ziehen oder klicken</p>
                        <input
                          type="file"
                          onChange={handleFileSelect}
                          className="absolute inset-0 opacity-0 cursor-pointer"
                          accept=".pdf,.doc,.docx,.txt,.md"
                          data-testid="file-input"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* P0.9: Import from exam_profile.seed_urls */}
                {sourceData.exam_ids?.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Database className="w-4 h-4 text-blue-600" />
                        <span className="text-sm text-blue-800">
                          Vorkonfigurierte URLs verfügbar
                        </span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          try {
                            const examId = sourceData.exam_ids[0];
                            const response = await fetch(`${API}/api/source-registry/wizard/seed-urls/${examId}`, {
                              headers: getAuthHeaders()
                            });
                            if (response.ok) {
                              const data = await response.json();
                              if (data.seed_urls?.length > 0) {
                                // Use first seed URL
                                setWebConfig({
                                  ...webConfig,
                                  canonical_url: data.seed_urls[0].url,
                                  crawl_policy: data.crawl_policy || webConfig.crawl_policy
                                });
                              }
                            }
                          } catch (e) {
                            console.error('Failed to import seed URLs:', e);
                          }
                        }}
                        data-testid="import-seed-urls-btn"
                      >
                        Importieren
                      </Button>
                    </div>
                  </div>
                )}
                
                <div>
                  <Label>URL *</Label>
                  <Input
                    value={webConfig.canonical_url}
                    onChange={(e) => setWebConfig({...webConfig, canonical_url: e.target.value})}
                    placeholder="https://example.com/page"
                    type="url"
                    data-testid="web-url-input"
                  />
                </div>
                
                <div>
                  <Label>Render-Modus</Label>
                  <Select 
                    value={webConfig.render_mode}
                    onValueChange={(v) => setWebConfig({...webConfig, render_mode: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="STATIC_HTTP">
                        Static HTTP (Standard, schnell)
                      </SelectItem>
                      <SelectItem value="HEADLESS_RENDER">
                        Headless Render (für JS-Seiten)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {mode === 'WEB_CRAWL_MULTI' && (
                  <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium flex items-center gap-2">
                      <Layers className="w-4 h-4" />
                      Crawl-Policy
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Max. Tiefe</Label>
                        <Input
                          type="number"
                          min={1}
                          max={5}
                          value={webConfig.crawl_policy.max_depth}
                          onChange={(e) => setWebConfig({
                            ...webConfig,
                            crawl_policy: {
                              ...webConfig.crawl_policy,
                              max_depth: parseInt(e.target.value) || 2
                            }
                          })}
                        />
                      </div>
                      <div>
                        <Label>Max. Seiten</Label>
                        <Input
                          type="number"
                          min={1}
                          max={100}
                          value={webConfig.crawl_policy.max_pages}
                          onChange={(e) => setWebConfig({
                            ...webConfig,
                            crawl_policy: {
                              ...webConfig.crawl_policy,
                              max_pages: parseInt(e.target.value) || 20
                            }
                          })}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
        
      case 3:
        return (
          <div className="space-y-4">
            {duplicateResult && (
              <>
                {duplicateResult.has_duplicate ? (
                  <div className={`p-4 rounded-lg ${
                    duplicateResult.match_type === 'EXACT' 
                      ? 'bg-yellow-50 border border-yellow-200'
                      : 'bg-blue-50 border border-blue-200'
                  }`}>
                    <div className="flex items-start gap-3">
                      <AlertTriangle className={`w-5 h-5 mt-0.5 ${
                        duplicateResult.match_type === 'EXACT' ? 'text-yellow-600' : 'text-blue-600'
                      }`} />
                      <div>
                        <p className="font-medium">{duplicateResult.message}</p>
                        {duplicateResult.existing_source_name && (
                          <p className="text-sm mt-1">
                            Bestehende Source: <strong>{duplicateResult.existing_source_name}</strong>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 mt-0.5 text-green-600" />
                      <p className="font-medium">{duplicateResult.message}</p>
                    </div>
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label>Aktion wählen:</Label>
                  
                  {duplicateResult.has_duplicate && (
                    <button
                      onClick={() => setSelectedAction('REUSE')}
                      className={`w-full p-3 rounded-lg border-2 text-left ${
                        selectedAction === 'REUSE'
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200'
                      }`}
                    >
                      <div className="font-medium">Bestehende Source wiederverwenden</div>
                      <div className="text-sm text-gray-500">
                        Keine neue Source erstellen
                      </div>
                    </button>
                  )}
                  
                  {duplicateResult.has_duplicate && duplicateResult.match_type === 'SIGNATURE' && (
                    <button
                      onClick={() => setSelectedAction('NEW_VERSION')}
                      className={`w-full p-3 rounded-lg border-2 text-left ${
                        selectedAction === 'NEW_VERSION'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200'
                      }`}
                    >
                      <div className="font-medium">Neue Version erstellen</div>
                      <div className="text-sm text-gray-500">
                        Verknüpft mit der bestehenden Source
                      </div>
                    </button>
                  )}
                  
                  <button
                    onClick={() => setSelectedAction('NEW_SOURCE')}
                    className={`w-full p-3 rounded-lg border-2 text-left ${
                      selectedAction === 'NEW_SOURCE'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200'
                    }`}
                  >
                    <div className="font-medium">Neue Source erstellen</div>
                    <div className="text-sm text-gray-500">
                      Unabhängige neue Source anlegen
                    </div>
                  </button>
                </div>
                
                {/* P0.7: AKU Extraction Toggle */}
                <div className="mt-6 pt-4 border-t">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="aku-extraction-toggle"
                      checked={runAkuExtraction}
                      onChange={(e) => setRunAkuExtraction(e.target.checked)}
                      disabled={!akuGateStatus?.can_enable_toggle || checkingAkuGate}
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                      data-testid="aku-extraction-toggle"
                    />
                    <div className="flex-1">
                      <label htmlFor="aku-extraction-toggle" className={`font-medium ${!akuGateStatus?.can_enable_toggle ? 'text-gray-400' : 'text-gray-900'}`}>
                        AKU-Extraktion aktivieren
                      </label>
                      <p className="text-sm text-gray-500">
                        Erstellt DRAFT-Vorschläge → ontology_proposals
                        <span className="block text-xs text-gray-400 mt-0.5">
                          (keine Veröffentlichung ohne Review/Kuratierung)
                        </span>
                      </p>
                      
                      {/* Gate Status Display */}
                      {checkingAkuGate ? (
                        <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          Prüfe Conflict Gate...
                        </div>
                      ) : akuGateStatus && !akuGateStatus.can_enable_toggle && (
                        <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                            <div className="text-sm">
                              <p className="font-medium text-yellow-800">{akuGateStatus.message}</p>
                              {akuGateStatus.admin_hint && (
                                <ul className="mt-1 text-yellow-700 text-xs space-y-0.5">
                                  {akuGateStatus.admin_hint.map((hint, i) => (
                                    <li key={`hint-${hint.slice(0, 15)}-${i}`}>• {hint}</li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {akuGateStatus?.can_enable_toggle && (
                        <div className="flex items-center gap-1 mt-1 text-xs text-green-600">
                          <CheckCircle className="w-3 h-3" />
                          {akuGateStatus.effective_sources_count} effektive Sources verfügbar
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        );
        
      case 4:
        return (
          <div className="space-y-4">
            {wizardResult && (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                
                <div>
                  <h3 className="text-xl font-bold text-green-700">Erfolgreich erstellt!</h3>
                  <p className="text-gray-600 mt-1">{wizardResult.message}</p>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4 text-left space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Source ID:</span>
                    <code className="text-sm bg-gray-200 px-2 py-0.5 rounded">
                      {wizardResult.source_id}
                    </code>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Canonical Name:</span>
                    <span className="font-mono text-sm">{wizardResult.canonical_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Display Name:</span>
                    <span>{wizardResult.display_name}</span>
                  </div>
                  {wizardResult.evidence_id && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Evidence ID:</span>
                      <code className="text-sm bg-gray-200 px-2 py-0.5 rounded">
                        {wizardResult.evidence_id}
                      </code>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-500">Index Status:</span>
                    <Badge variant="outline">{wizardResult.index_status}</Badge>
                  </div>
                </div>
                
                {/* P0.7: AKU Extraction Result */}
                {wizardResult.aku_extraction && (
                  <div className={`rounded-lg p-4 text-left ${
                    wizardResult.aku_extraction.status === 'SUCCESS' 
                      ? 'bg-green-50 border border-green-200'
                      : wizardResult.aku_extraction.status === 'BLOCKED'
                        ? 'bg-yellow-50 border border-yellow-200'
                        : 'bg-gray-50 border border-gray-200'
                  }`}>
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      {wizardResult.aku_extraction.status === 'SUCCESS' ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : wizardResult.aku_extraction.status === 'BLOCKED' ? (
                        <AlertTriangle className="w-4 h-4 text-yellow-600" />
                      ) : (
                        <FileText className="w-4 h-4 text-gray-500" />
                      )}
                      AKU-Extraktion
                    </h4>
                    
                    {wizardResult.aku_extraction.status === 'SUCCESS' && (
                      <div className="text-sm space-y-1">
                        <p className="text-green-700">{wizardResult.aku_extraction.message}</p>
                        <div className="text-green-600">
                          {wizardResult.aku_extraction.chunks_processed} Chunks verarbeitet → {wizardResult.aku_extraction.proposals_created} Proposals
                        </div>
                      </div>
                    )}
                    
                    {wizardResult.aku_extraction.status === 'BLOCKED' && (
                      <div className="text-sm space-y-2">
                        <p className="text-yellow-800">{wizardResult.aku_extraction.message}</p>
                        {wizardResult.aku_extraction.admin_hint && (
                          <ul className="text-yellow-700 text-xs space-y-0.5">
                            {wizardResult.aku_extraction.admin_hint.map((hint, i) => (
                              <li key={`aku-hint-${hint.slice(0, 15)}-${i}`}>• {hint}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                    
                    {(wizardResult.aku_extraction.status === 'SKIPPED' || wizardResult.aku_extraction.status === 'ERROR') && (
                      <p className="text-sm text-gray-600">
                        {wizardResult.aku_extraction.reason || wizardResult.aku_extraction.error || 'Übersprungen'}
                      </p>
                    )}
                  </div>
                )}
                
                <div className="flex gap-2 justify-center pt-2">
                  <Button variant="outline" onClick={() => {
                    resetWizard();
                    onClose();
                  }}>
                    Schließen
                  </Button>
                  <Button onClick={resetWizard}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Weitere Source hinzufügen
                  </Button>
                </div>
              </div>
            )}
          </div>
        );
        
      default:
        return null;
    }
  };
  
  // Step labels
  const steps = [
    { label: 'Modus', icon: Shield },
    { label: 'Source', icon: FileText },
    { label: 'Evidence', icon: Upload },
    { label: 'Prüfung', icon: AlertTriangle },
    { label: 'Fertig', icon: CheckCircle }
  ];
  
  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) { resetWizard(); onClose(); }}}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-600" />
            Source & Evidence hinzufügen
          </DialogTitle>
        </DialogHeader>
        
        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-6">
          {steps.map((s, idx) => {
            const Icon = s.icon;
            const isActive = idx === step;
            const isCompleted = idx < step;
            return (
              <div key={s.label || `step-${idx}`} className="flex items-center">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                  isCompleted 
                    ? 'bg-green-500 text-white'
                    : isActive
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-200 text-gray-500'
                }`}>
                  {isCompleted ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <Icon className="w-4 h-4" />
                  )}
                </div>
                {idx < steps.length - 1 && (
                  <div className={`w-12 h-0.5 mx-1 ${
                    isCompleted ? 'bg-green-500' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            );
          })}
        </div>
        
        {/* Step Content */}
        <div className="min-h-[300px]">
          {renderStepContent()}
        </div>
        
        {/* Navigation */}
        {step < 4 && (
          <div className="flex justify-between mt-6 pt-4 border-t">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={step === 0 || loading}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zurück
            </Button>
            
            <Button
              onClick={handleNext}
              disabled={!canProceed() || loading}
              data-testid="wizard-next"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Verarbeite...
                </>
              ) : step === 3 ? (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Ausführen
                </>
              ) : (
                <>
                  Weiter
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SourceEvidenceWizard;
